import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  ClipboardList, Plus, X, Loader, CheckCircle, AlertCircle,
  Warehouse, Barcode, ArrowLeft, Check, Ban, Snowflake, ScanLine,
  TrendingUp, TrendingDown, Package, RefreshCw, FileText, FileSpreadsheet,
  User, ShieldCheck, MapPin, ArrowRight, Search,
} from 'lucide-react';
import {
  getEntrepots, getProduits,
  getInventaires, getInventaire, createInventaire,
  saisirLignesInventaire, ajouterLigneInventaire, changerEmplacementLigneInventaire,
  validerInventaire, annulerInventaire, getInventairePDF,
} from '../services/api';
import './Inventaire.css';

const fdate = (d) => d
  ? new Date(d).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })
  : '—';

const nomUser = (u) => u ? `${u.prenom || ''} ${u.nom || ''}`.trim() || '—' : '—';

// Clé de filtre/comparaison d'un emplacement ('__none__' = non rangé)
const cleEmp = (id) => (id ? String(id) : '__none__');

// Où le stock de la ligne est déclaré aujourd'hui
const emplacementActuel = (l) => {
  const emps = l.emplacements || [];
  if (emps.length === 0) return 'Non rangé';
  if (emps.length === 1) return emps[0].libelle;
  return `${emps.length} emplacements`;
};

// Valeur du sélecteur : '' = ne pas déplacer, sinon la cible choisie
const valeurEmp = (l) => (l.emplacementModifie ? cleEmp(l.emplacementCible) : '');

// Emplacement qui sert de clé de tri : la correction saisie prime sur le gel.
const emplacementTri = (l) => (l.emplacementModifie
  ? (l.libelleCible || 'Non rangé')
  : ((l.emplacements || [])[0]?.libelle || 'Non rangé'));

// Tri alphabétique naturel : A2 vient avant A10.
const compareNom = (a, b) => (a || '').localeCompare(b || '', 'fr', { numeric: true, sensitivity: 'base' });

// Ordre de parcours de l'entrepôt : emplacements A → Z, « non rangé » en dernier.
const comparerParEmplacement = (a, b) => {
  const la = emplacementTri(a), lb = emplacementTri(b);
  const na = la === 'Non rangé', nb = lb === 'Non rangé';
  if (na !== nb) return na ? 1 : -1;
  return compareNom(la, lb) || compareNom(a.nomProduit, b.nomProduit);
};

const STATUTS = {
  en_cours: { label: 'En cours', color:'#d97706', bg:'#fef3c7' },
  valide:   { label: 'Validé',   color:'#16a34a', bg:'#dcfce7' },
  annule:   { label: 'Annulé',   color:'#64748b', bg:'#f1f5f9' },
};

export default function Inventaire() {
  const [vue, setVue] = useState('liste');           // 'liste' | 'saisie'
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [inventaires, setInventaires] = useState([]);
  const [entrepots, setEntrepots] = useState([]);
  // Recherche d'un produit dans les inventaires (vue liste)
  const [rechercheProduit, setRechercheProduit] = useState('');

  // Création
  const [showCreate, setShowCreate] = useState(false);
  const [newEntrepot, setNewEntrepot] = useState('');
  const [newNote, setNewNote] = useState('');
  const [creating, setCreating] = useState(false);

  // Saisie
  const [courant, setCourant] = useState(null);       // inventaire ouvert
  const [lignes, setLignes] = useState([]);
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState('toutes');     // toutes | restantes | comptees | ecarts | rangements
  const [filtreEmp, setFiltreEmp] = useState('');     // '' = tous les emplacements
  const [tri, setTri] = useState('produit');          // produit | emplacement
  const [plan, setPlan] = useState([]);               // rayons/emplacements de l'entrepôt
  const [saving, setSaving] = useState({});           // { [produitId]: 'pending'|'ok'|'err' }
  const [empSaving, setEmpSaving] = useState({});     // idem pour le rangement
  const [showEcarts, setShowEcarts] = useState(false);
  const [confirmation, setConfirmation] = useState(false);   // 2e étape avant validation définitive
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Produits du catalogue (pour ajouter une référence absente du gel)
  const [catalogue, setCatalogue] = useState([]);

  const rechercheRef = useRef(null);
  const timers = useRef({});

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => { chargerListe(); /* eslint-disable-next-line */ }, []);

  // Relance la liste (filtrée par produit) 300 ms après la dernière frappe
  const premierRendu = useRef(true);
  useEffect(() => {
    if (premierRendu.current) { premierRendu.current = false; return; }
    const t = setTimeout(() => chargerInventaires(rechercheProduit), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [rechercheProduit]);

  const chargerInventaires = async (q) => {
    try {
      const { data } = await getInventaires(q.trim() ? { q: q.trim() } : undefined);
      setInventaires(data.data || []);
    } catch { showToast('error', 'Erreur lors de la recherche'); }
  };

  const chargerListe = async () => {
    setLoading(true);
    try {
      const q = rechercheProduit.trim();
      const [invRes, entRes] = await Promise.all([getInventaires(q ? { q } : undefined), getEntrepots()]);
      setInventaires(invRes.data.data || []);
      const ents = entRes.data.data || entRes.data || [];
      setEntrepots(Array.isArray(ents) ? ents.filter(e => e.actif !== false) : []);
    } catch { showToast('error', 'Erreur de chargement'); }
    finally { setLoading(false); }
  };

  // ── Création avec gel du stock théorique ──
  const lancerInventaire = async () => {
    if (!newEntrepot) { showToast('error', 'Choisissez un entrepôt'); return; }
    setCreating(true);
    try {
      const { data } = await createInventaire({ entrepot: newEntrepot, note: newNote });
      showToast('success', data.message);
      setShowCreate(false); setNewEntrepot(''); setNewNote('');
      await ouvrirSaisie(data.data._id);
      chargerListe();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors de la création');
    } finally { setCreating(false); }
  };

  // ── Ouvrir la saisie ──
  const ouvrirSaisie = async (id, rechercheInitiale = '') => {
    setLoading(true);
    try {
      const [invRes, prodRes] = await Promise.all([getInventaire(id), getProduits()]);
      setCourant(invRes.data.data);
      setLignes(invRes.data.data.lignes || []);
      setPlan(invRes.data.data.plan || []);
      setCatalogue(prodRes.data.data || []);
      setVue('saisie'); setRecherche(rechercheInitiale); setFiltre('toutes'); setFiltreEmp(''); setShowEcarts(false);
      setTimeout(() => rechercheRef.current?.focus(), 100);
    } catch { showToast('error', 'Impossible d\'ouvrir cet inventaire'); }
    finally { setLoading(false); }
  };

  const retourListe = () => {
    setVue('liste'); setCourant(null); setLignes([]); setPlan([]); setEmpSaving({});
    chargerListe();
  };

  // Liste plate des emplacements du plan (sélecteur + filtre)
  const emplacementsPlan = useMemo(() => {
    const list = [];
    for (const r of plan) {
      for (const e of r.emplacements || []) {
        list.push({ id: String(e._id), nom: e.nom, rayon: r.nom, label: `${r.nom} › ${e.nom}` });
      }
    }
    return list;
  }, [plan]);

  // ── Correction du rangement : où l'article se trouve réellement ──
  // Appliqué tout de suite, contrairement aux quantités : tout le stock du
  // produit (y compris celui logé dans d'autres entrepôts) rejoint cet
  // emplacement, pour que la correction soit visible partout dans l'application.
  const changerEmplacement = async (produitId, valeur) => {
    const avant = lignes.find(l => String(l.produit) === String(produitId));
    const memoire = {
      emplacementModifie: avant?.emplacementModifie || false,
      emplacementCible: avant?.emplacementCible ?? null,
      libelleCible: avant?.libelleCible,
      emplacements: avant?.emplacements,
      rangementApplique: avant?.rangementApplique,
    };
    const libelle = valeur === '__none__'
      ? 'Non rangé'
      : (emplacementsPlan.find(e => e.id === valeur)?.label || '');

    setLignes(prev => prev.map(l => String(l.produit) === String(produitId) ? {
      ...l,
      emplacementModifie: valeur !== '',
      emplacementCible: valeur === '' || valeur === '__none__' ? null : valeur,
      libelleCible: valeur === '' ? undefined : libelle,
    } : l));
    setEmpSaving(s => ({ ...s, [produitId]: 'pending' }));

    try {
      const { data } = await changerEmplacementLigneInventaire(courant._id,
        valeur === ''
          ? { produit: produitId, reset: true }
          : { produit: produitId, emplacement: valeur === '__none__' ? null : valeur });
      // Le serveur a déplacé le stock : la ligne reprend la localisation réelle,
      // pas celle qui était gelée au démarrage du comptage.
      const maj = data.data || {};
      setLignes(prev => prev.map(l => String(l.produit) === String(produitId) ? {
        ...l,
        emplacements: maj.emplacements || [],
        rangementApplique: maj.rangementApplique || false,
      } : l));
      setEmpSaving(s => ({ ...s, [produitId]: 'ok' }));
      setTimeout(() => setEmpSaving(s => { const c = { ...s }; delete c[produitId]; return c; }), 1200);
      showToast('success', data.message);
    } catch (err) {
      // La ligne reprend son état d'avant : l'écran ne doit pas montrer un
      // rangement que le serveur a refusé.
      setLignes(prev => prev.map(l => String(l.produit) === String(produitId) ? { ...l, ...memoire } : l));
      setEmpSaving(s => ({ ...s, [produitId]: 'err' }));
      showToast('error', err.response?.data?.message || 'Erreur lors du rangement');
    }
  };

  // ── Saisie d'une quantité, sauvegardée progressivement ──
  const saisir = (produitId, valeur) => {
    setLignes(prev => prev.map(l =>
      String(l.produit) === String(produitId)
        ? { ...l, stockCompte: valeur === '' ? null : Number(valeur),
            ecart: valeur === '' ? null : Number(valeur) - (l.stockTheorique || 0) }
        : l
    ));

    // Débounce : on n'envoie qu'après une pause de frappe, mais chaque ligne
    // est persistée indépendamment — une interruption ne perd que la saisie en cours.
    clearTimeout(timers.current[produitId]);
    setSaving(s => ({ ...s, [produitId]: 'pending' }));
    timers.current[produitId] = setTimeout(async () => {
      try {
        await saisirLignesInventaire(courant._id, {
          produit: produitId,
          stockCompte: valeur === '' ? null : Number(valeur),
        });
        setSaving(s => ({ ...s, [produitId]: 'ok' }));
        setTimeout(() => setSaving(s => { const c = { ...s }; delete c[produitId]; return c; }), 1200);
      } catch {
        setSaving(s => ({ ...s, [produitId]: 'err' }));
      }
    }, 500);
  };

  // ── Ajouter une référence absente du gel ──
  const ajouterReference = async (produit) => {
    try {
      await ajouterLigneInventaire(courant._id, { produit: produit._id, stockCompte: null });
      const { data } = await getInventaire(courant._id);
      setLignes(data.data.lignes || []);
      setRecherche('');
      showToast('success', `${produit.nom} ajouté à l'inventaire`);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur');
    }
  };

  // ── Validation : confirmation obligatoire en deux temps ──
  const valider = async () => {
    setBusy(true);
    try {
      const { data } = await validerInventaire(courant._id);
      showToast('success', data.message);
      setConfirmation(false); setShowEcarts(false);
      retourListe();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors de la validation');
    } finally { setBusy(false); }
  };

  const annuler = async () => {
    if (!window.confirm('Annuler cet inventaire ? Aucun stock ne sera modifié.')) return;
    setBusy(true);
    try {
      await annulerInventaire(courant._id);
      showToast('success', 'Inventaire annulé');
      retourListe();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur');
    } finally { setBusy(false); }
  };

  // ── Exports ──
  const exporterPDF = async (id) => {
    setPdfBusy(true);
    try {
      const res = await getInventairePDF(id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { showToast('error', 'Erreur lors de la génération du PDF'); }
    finally { setPdfBusy(false); }
  };

  const exporterExcel = (inv, lignesInv) => {
    const estValide = inv.statut === 'valide';
    const entete = [
      [`Inventaire ${inv.numero}`],
      [`Entrepôt : ${inv.nomEntrepot || '—'}`],
      [`Créé par : ${nomUser(inv.creePar)} — ${fdate(inv.dateDebut)}`],
      [estValide
        ? `Validé par : ${nomUser(inv.validePar)} — ${fdate(inv.dateValidation)}`
        : `Statut : ${(STATUTS[inv.statut] || {}).label || inv.statut}`],
      [`Export du ${fdate(new Date())}`],
      [],
    ];
    const colonnes = estValide
      ? ['Référence', 'Produit', 'Code-barres', 'Emplacement', 'Rangement corrigé', 'Stock théorique', 'Stock compté', 'Écart', 'Ajustement appliqué']
      : ['Référence', 'Produit', 'Code-barres', 'Emplacement', 'Rangement corrigé', 'Stock théorique', 'Stock compté', 'Écart'];

    const corps = lignesInv.map(l => {
      const base = [
        l.codeProduit || '',
        l.nomProduit || '',
        l.codeBarres || '',
        emplacementActuel(l),
        // Le rangement corrigé est déjà appliqué : la colonne dit qu'il l'a été,
        // la colonne « Emplacement » montre déjà le nouvel emplacement.
        l.emplacementModifie ? `Oui — ${l.libelleCible || 'Non rangé'}` : '',
        l.stockTheorique ?? 0,
        l.stockCompte == null ? '' : l.stockCompte,
        l.stockCompte == null ? '' : l.stockCompte - (l.stockTheorique || 0),
      ];
      return estValide ? [...base, l.deltaApplique == null ? '' : l.deltaApplique] : base;
    });

    const s = synthese;
    const pied = [
      [],
      ['', 'TOTAUX', '', '', `${s.rangements} rangement(s)`, '', `${s.comptees}/${s.nbLignes} comptées`, s.ecartNet],
      ['', `Surplus : +${s.ecartPos}`, '', '', '', '', `Manquants : ${s.ecartNeg}`, ''],
    ];

    const ws = XLSX.utils.aoa_to_sheet([...entete, colonnes, ...corps, ...pied]);
    ws['!cols'] = [{ wch:16 }, { wch:38 }, { wch:16 }, { wch:24 }, { wch:24 }, { wch:15 }, { wch:14 }, { wch:10 }, { wch:18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventaire');
    XLSX.writeFile(wb, `inventaire-${inv.numero}.xlsx`);
  };

  // ── Recherche : référence, désignation, code-barres ──
  const lignesFiltrees = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    let r = lignes;
    if (q) {
      r = r.filter(l =>
        (l.codeProduit || '').toLowerCase().includes(q) ||
        (l.nomProduit  || '').toLowerCase().includes(q) ||
        (l.codeBarres  || '').toLowerCase().includes(q)
      );
    }
    if (filtre === 'restantes')  r = r.filter(l => l.stockCompte == null);
    if (filtre === 'comptees')   r = r.filter(l => l.stockCompte != null);
    if (filtre === 'ecarts')     r = r.filter(l => l.stockCompte != null && l.ecart !== 0);
    if (filtre === 'rangements') r = r.filter(l => l.emplacementModifie);
    // Comptage allée par allée : on ne garde que ce qui est (ou ira) à cet endroit.
    if (filtreEmp) {
      r = r.filter(l => l.emplacementModifie
        ? cleEmp(l.emplacementCible) === filtreEmp
        : ((l.emplacements || []).length
            ? l.emplacements.some(e => cleEmp(e.emplacement) === filtreEmp)
            : filtreEmp === '__none__'));
    }

    // Tri par emplacement : le comptage suit alors le parcours physique de
    // l'entrepôt, rayon par rayon.
    if (tri === 'emplacement') r = [...r].sort(comparerParEmplacement);
    return r;
  }, [lignes, recherche, filtre, filtreEmp, tri]);

  // Produits du catalogue absents du gel, correspondant à la recherche
  const suggestions = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q || lignesFiltrees.length > 0) return [];
    const presents = new Set(lignes.map(l => String(l.produit)));
    return catalogue.filter(p =>
      !presents.has(String(p._id)) && (
        (p.codeProduit || '').toLowerCase().includes(q) ||
        (p.nom         || '').toLowerCase().includes(q) ||
        (p.codeBarres  || '').toLowerCase().includes(q)
      )
    ).slice(0, 8);
  }, [recherche, lignesFiltrees, lignes, catalogue]);

  const synthese = useMemo(() => {
    let comptees = 0, ecartPos = 0, ecartNeg = 0, avecEcart = 0, rangements = 0;
    for (const l of lignes) {
      if (l.emplacementModifie) rangements++;
      if (l.stockCompte == null) continue;
      comptees++;
      const e = l.stockCompte - (l.stockTheorique || 0);
      if (e > 0) { ecartPos += e; avecEcart++; }
      else if (e < 0) { ecartNeg += e; avecEcart++; }
    }
    return {
      nbLignes: lignes.length, comptees, restantes: lignes.length - comptees,
      avecEcart, ecartPos, ecartNeg, ecartNet: ecartPos + ecartNeg, rangements,
    };
  }, [lignes]);

  const lignesRangees = useMemo(() => lignes.filter(l => l.emplacementModifie), [lignes]);

  // Scanner code-barres : la lecture se termine par Entrée → focus sur la ligne unique
  const onRechercheKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (lignesFiltrees.length === 1) {
      const id = lignesFiltrees[0].produit;
      document.getElementById(`inv-qte-${id}`)?.focus();
      document.getElementById(`inv-qte-${id}`)?.select();
    }
  };

  if (loading) return (
    <div className="inv-loading"><Loader className="inv-spin" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="inv-page">
      {toast && (
        <div className={`inv-toast inv-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={14}/> : <AlertCircle size={14}/>}
          {toast.text}
        </div>
      )}

      {/* ══════════ LISTE ══════════ */}
      {vue === 'liste' && (
        <>
          <div className="inv-header">
            <div className="inv-header-left">
              <div className="inv-header-icon"><ClipboardList size={20}/></div>
              <div>
                <h1 className="inv-header-title">Inventaire</h1>
                <p className="inv-header-sub">Comptage physique du stock par entrepôt</p>
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="inv-btn-ghost" onClick={chargerListe}><RefreshCw size={14}/> Actualiser</button>
              <button className="inv-btn-primary" onClick={() => setShowCreate(true)} disabled={!entrepots.length}>
                <Plus size={15}/> Nouvel inventaire
              </button>
            </div>
          </div>

          <div className="inv-content">
            <div className="inv-filters" style={{ marginBottom: 14 }}>
              <div className="inv-search-wrap">
                <Search size={14} className="inv-search-ico"/>
                <input
                  className="inv-search"
                  placeholder="Rechercher un produit (nom, référence, code-barres) pour voir dans quels inventaires il figure…"
                  value={rechercheProduit}
                  onChange={e => setRechercheProduit(e.target.value)}/>
                {rechercheProduit && <button className="inv-search-clear" onClick={() => setRechercheProduit('')}><X size={13}/></button>}
              </div>
            </div>

            {rechercheProduit.trim() && inventaires.length === 0 ? (
              <div className="inv-empty">
                <Search size={32}/>
                <h3>Aucun inventaire trouvé</h3>
                <p>Aucun inventaire ne contient un produit correspondant à « {rechercheProduit.trim()} ».</p>
              </div>
            ) : inventaires.length === 0 ? (
              <div className="inv-empty">
                <ClipboardList size={32}/>
                <h3>Aucun inventaire</h3>
                <p>Lancez un inventaire pour compter physiquement le stock d'un entrepôt.</p>
                <button className="inv-btn-primary" onClick={() => setShowCreate(true)} disabled={!entrepots.length}>
                  <Plus size={14}/> Nouvel inventaire
                </button>
              </div>
            ) : (
              <div className="inv-card">
                <table className="inv-table">
                  <thead>
                    <tr>
                      <th>N°</th><th>Entrepôt</th><th>Statut</th>
                      {rechercheProduit.trim() && <th>Produit trouvé</th>}
                      <th>Avancement</th><th>Écart net</th>
                      <th>Créé par</th><th>Validé par</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventaires.map(i => {
                      const st = STATUTS[i.statut] || STATUTS.annule;
                      const s = i.synthese || {};
                      const pct = s.nbLignes ? Math.round((s.nbComptees / s.nbLignes) * 100) : 0;
                      return (
                        <tr key={i._id}>
                          <td><span className="inv-num">{i.numero}</span></td>
                          <td><span className="inv-ent"><Warehouse size={12}/> {i.nomEntrepot}</span></td>
                          <td><span className="inv-badge" style={{ background:st.bg, color:st.color }}>{st.label}</span></td>
                          {rechercheProduit.trim() && (
                            <td>
                              {(i.correspondances || []).map(c => (
                                <div key={c.produit} className="inv-match">
                                  <span className="inv-match-nom"><Package size={11}/> {c.nomProduit}</span>
                                  {c.codeProduit && <span className="inv-muted"> · {c.codeProduit}</span>}
                                  <div className="inv-user-date">
                                    Théorique {c.stockTheorique} · Compté {c.stockCompte ?? '—'}
                                    {c.ecart != null && c.ecart !== 0 && (
                                      <span className={`inv-ecart ${c.ecart > 0 ? 'inv-ecart--pos' : 'inv-ecart--neg'}`}> ({c.ecart > 0 ? '+' : ''}{c.ecart})</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </td>
                          )}
                          <td>
                            <div className="inv-progress">
                              <div className="inv-progress-track">
                                <div className="inv-progress-fill" style={{ width:`${pct}%` }}/>
                              </div>
                              <span>{s.nbComptees || 0}/{s.nbLignes || 0}</span>
                            </div>
                          </td>
                          <td>
                            {s.ecartNet == null ? <span className="inv-muted">—</span> : (
                              <span className={`inv-ecart ${s.ecartNet > 0 ? 'inv-ecart--pos' : s.ecartNet < 0 ? 'inv-ecart--neg' : ''}`}>
                                {s.ecartNet > 0 ? '+' : ''}{s.ecartNet}
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="inv-user"><User size={11}/> {nomUser(i.creePar)}</div>
                            <div className="inv-user-date">{fdate(i.dateDebut)}</div>
                          </td>
                          <td>
                            {i.statut === 'valide' ? (
                              <>
                                <div className="inv-user"><ShieldCheck size={11}/> {nomUser(i.validePar)}</div>
                                <div className="inv-user-date">{fdate(i.dateValidation)}</div>
                              </>
                            ) : <span className="inv-muted">—</span>}
                          </td>
                          <td>
                            <div className="inv-row-actions">
                              <button className="inv-btn-open" onClick={() => ouvrirSaisie(i._id, rechercheProduit.trim())}>
                                {i.statut === 'en_cours' ? 'Continuer' : 'Consulter'}
                              </button>
                              <button className="inv-btn-icon" title="Imprimer le PDF"
                                onClick={() => exporterPDF(i._id)} disabled={pdfBusy}>
                                <FileText size={13}/>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ══════════ SAISIE ══════════ */}
      {vue === 'saisie' && courant && (
        <>
          <div className="inv-header">
            <div className="inv-header-left">
              <button className="inv-back" onClick={retourListe}><ArrowLeft size={16}/></button>
              <div>
                <h1 className="inv-header-title">{courant.numero}</h1>
                <p className="inv-header-sub">
                  <Warehouse size={11}/> {courant.nomEntrepot}
                  <span className="inv-badge" style={{
                    marginLeft:8,
                    background:(STATUTS[courant.statut]||STATUTS.annule).bg,
                    color:(STATUTS[courant.statut]||STATUTS.annule).color }}>
                    {(STATUTS[courant.statut]||STATUTS.annule).label}
                  </span>
                </p>
              </div>
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button className="inv-btn-ghost"
                onClick={() => exporterExcel(courant, tri === 'emplacement' ? [...lignes].sort(comparerParEmplacement) : lignes)}>
                <FileSpreadsheet size={14}/> Excel
              </button>
              <button className="inv-btn-ghost" onClick={() => exporterPDF(courant._id)} disabled={pdfBusy}>
                {pdfBusy ? <Loader size={14} className="inv-spin"/> : <FileText size={14}/>} PDF
              </button>
              {courant.statut === 'en_cours' && (
                <>
                  <button className="inv-btn-ghost" onClick={annuler} disabled={busy}><Ban size={14}/> Annuler</button>
                  <button className="inv-btn-ecarts" onClick={() => { setConfirmation(false); setShowEcarts(true); }}
                    disabled={!synthese.comptees && !synthese.rangements}>
                    Vérifier les écarts ({synthese.avecEcart})
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="inv-content">
            <div className="inv-frozen">
              <Snowflake size={13}/>
              <span>
                Stock théorique <strong>gelé</strong> au {fdate(courant.dateDebut)} — il reste la référence
                même si des entrées ou sorties ont lieu pendant le comptage.
              </span>
            </div>

            {courant.statut === 'en_cours' && (
              <div className="inv-frozen">
                <MapPin size={13}/>
                <span>
                  Les <strong>rangements corrigés s'appliquent immédiatement</strong> : tout le stock de la
                  référence — y compris celui logé dans un autre entrepôt — rejoint l'emplacement constaté,
                  et le bon emplacement s'affiche partout dans l'application. « Ne pas corriger » remet tout en place.
                </span>
              </div>
            )}

            {courant.statut === 'en_cours' && emplacementsPlan.length === 0 && (
              <div className="inv-warn">
                <AlertCircle size={13}/>
                <span>
                  Aucun emplacement n'est défini pour <strong>{courant.nomEntrepot}</strong> : créez vos rayons
                  et emplacements dans <strong>Entrepôts › Plan</strong> pour pouvoir corriger le rangement
                  pendant le comptage.
                </span>
              </div>
            )}

            {/* Traçabilité */}
            <div className="inv-tracabilite">
              <div className="inv-trace-item">
                <span><User size={11}/> Créé par</span>
                <strong>{nomUser(courant.creePar)}</strong>
                <em>{fdate(courant.dateDebut)}</em>
              </div>
              <div className="inv-trace-item">
                <span><ShieldCheck size={11}/> Validé par</span>
                {courant.statut === 'valide' ? (
                  <>
                    <strong>{nomUser(courant.validePar)}</strong>
                    <em>{fdate(courant.dateValidation)}</em>
                  </>
                ) : <strong className="inv-muted">Non validé</strong>}
              </div>
              {courant.note && (
                <div className="inv-trace-item">
                  <span>Note</span>
                  <strong>{courant.note}</strong>
                </div>
              )}
            </div>

            <div className="inv-kpi-row">
              {[
                { label:'Références', value: synthese.nbLignes,  color:'#6366f1', bg:'#eef2ff', icon: Package },
                { label:'Comptées',   value: synthese.comptees,  color:'#16a34a', bg:'#dcfce7', icon: Check },
                { label:'Restantes',  value: synthese.restantes, color:'#d97706', bg:'#fef3c7', icon: ClipboardList },
                { label:'Surplus',    value: `+${synthese.ecartPos}`, color:'#16a34a', bg:'#dcfce7', icon: TrendingUp },
                { label:'Manquants',  value: synthese.ecartNeg,  color:'#dc2626', bg:'#fee2e2', icon: TrendingDown },
                { label:'Rangements', value: synthese.rangements, color:'#0ea5e9', bg:'#e0f2fe', icon: MapPin },
              ].map((k,i) => {
                const Icon = k.icon;
                return (
                  <div key={i} className="inv-kpi" style={{ borderTopColor:k.color }}>
                    <div className="inv-kpi-icon" style={{ background:k.bg, color:k.color }}><Icon size={13}/></div>
                    <div className="inv-kpi-val">{k.value}</div>
                    <div className="inv-kpi-label">{k.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="inv-filters">
              <div className="inv-search-wrap">
                <ScanLine size={14} className="inv-search-ico"/>
                <input
                  ref={rechercheRef}
                  className="inv-search"
                  placeholder="Référence, désignation ou scan du code-barres…"
                  value={recherche}
                  onChange={e => setRecherche(e.target.value)}
                  onKeyDown={onRechercheKeyDown}/>
                {recherche && <button className="inv-search-clear" onClick={() => setRecherche('')}><X size={13}/></button>}
              </div>
              <div className="inv-tabs">
                {[['toutes','Toutes'],['restantes','Restantes'],['comptees','Comptées'],['ecarts','Écarts'],['rangements',`Rangements${synthese.rangements ? ` (${synthese.rangements})` : ''}`]].map(([k,lab]) => (
                  <button key={k} className={`inv-tab ${filtre===k ? 'inv-tab--on' : ''}`} onClick={() => setFiltre(k)}>
                    {lab}
                  </button>
                ))}
              </div>
              {/* Ordre d'affichage : produit, ou parcours de l'entrepôt */}
              <select className="inv-emp-filtre" value={tri} onChange={e => setTri(e.target.value)}
                title="Ordre d'affichage des lignes">
                <option value="produit">Trier : produit (A → Z)</option>
                <option value="emplacement">Trier : emplacement (A → Z)</option>
              </select>
              {/* Comptage allée par allée */}
              <select className="inv-emp-filtre" value={filtreEmp} onChange={e => setFiltreEmp(e.target.value)}>
                <option value="">Tous les emplacements</option>
                <option value="__none__">Non rangé</option>
                {plan.map(r => (
                  <optgroup key={r._id} label={r.nom}>
                    {(r.emplacements || []).map(e => (
                      <option key={e._id} value={String(e._id)}>{e.nom}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <span className="inv-count">{lignesFiltrees.length} ligne(s)</span>
            </div>

            {/* Référence absente du gel */}
            {suggestions.length > 0 && (
              <div className="inv-suggest">
                <div className="inv-suggest-head">
                  <AlertCircle size={13}/> Non référencé dans cet entrepôt — à ajouter au comptage ?
                </div>
                {suggestions.map(p => (
                  <button key={p._id} className="inv-suggest-line" onClick={() => ajouterReference(p)}>
                    <Package size={12}/>
                    <span className="inv-prod-ref">{p.codeProduit || '—'}</span>
                    <span className="inv-prod-nom">{p.nom}</span>
                    <span className="inv-suggest-add"><Plus size={12}/> Ajouter</span>
                  </button>
                ))}
              </div>
            )}

            <div className="inv-card">
              <table className="inv-table inv-table--saisie">
                <thead>
                  <tr>
                    <th>Référence</th><th>Produit</th>
                    <th>Emplacement</th>
                    <th className="inv-th-num">Stock théorique</th>
                    <th className="inv-th-num">Stock compté</th>
                    <th className="inv-th-num">Écart</th>
                    {courant.statut === 'valide' && <th className="inv-th-num">Ajustement</th>}
                  </tr>
                </thead>
                <tbody>
                  {lignesFiltrees.length === 0 ? (
                    <tr><td colSpan={courant.statut === 'valide' ? 7 : 6} className="inv-td-empty">
                      {recherche ? `Aucune référence pour « ${recherche} »` : 'Aucune ligne'}
                    </td></tr>
                  ) : lignesFiltrees.map(l => {
                    const etat = saving[String(l.produit)];
                    const etatEmp = empSaving[String(l.produit)];
                    return (
                      <tr key={String(l.produit)} className={l.stockCompte != null ? 'inv-row--compte' : ''}>
                        <td>
                          <span className="inv-prod-ref">{l.codeProduit || '—'}</span>
                          {l.ajoutee && <span className="inv-tag-ajout">ajouté</span>}
                        </td>
                        <td>
                          <span className="inv-prod-nom">{l.nomProduit}</span>
                          {l.codeBarres && <span className="inv-prod-cb"><Barcode size={9}/> {l.codeBarres}</span>}
                        </td>
                        <td className="inv-emp-cell">
                          <span className="inv-emp-actuel"
                            title={(l.emplacements || []).map(e => `${e.libelle} : ${e.quantite}`).join('\n') || undefined}>
                            <MapPin size={10}/> {emplacementActuel(l)}
                          </span>
                          {courant.statut === 'en_cours' ? (
                            <div className="inv-emp-edit">
                              <select
                                className={`inv-emp-select${l.emplacementModifie ? ' inv-emp-select--on' : ''}`}
                                value={valeurEmp(l)}
                                disabled={etatEmp === 'pending'}
                                onChange={e => changerEmplacement(l.produit, e.target.value)}>
                                <option value="">— Ne pas corriger —</option>
                                <option value="__none__">Non rangé</option>
                                {/* Cible désactivée depuis la saisie : elle doit rester visible */}
                                {l.emplacementModifie && l.emplacementCible
                                  && !emplacementsPlan.some(e => e.id === String(l.emplacementCible)) && (
                                  <option value={String(l.emplacementCible)}>{l.libelleCible || 'Emplacement'}</option>
                                )}
                                {plan.map(r => (
                                  <optgroup key={r._id} label={r.nom}>
                                    {(r.emplacements || []).map(e => (
                                      <option key={e._id} value={String(e._id)}>{e.nom}</option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                              {etatEmp === 'pending' && <Loader size={11} className="inv-spin inv-save-ico"/>}
                              {etatEmp === 'ok'      && <Check  size={11} className="inv-save-ico inv-save-ico--ok"/>}
                              {etatEmp === 'err'     && <AlertCircle size={11} className="inv-save-ico inv-save-ico--err"/>}
                            </div>
                          ) : l.emplacementModifie && (
                            // Rangement déjà appliqué : la flèche n'a de sens que si
                            // le stock a rebougé depuis la correction.
                            <span className="inv-emp-new">
                              {emplacementActuel(l) === (l.libelleCible || 'Non rangé')
                                ? <em>rangement corrigé</em>
                                : <><ArrowRight size={10}/> {l.libelleCible || 'Non rangé'}</>}
                            </span>
                          )}
                        </td>
                        <td className="inv-th-num"><span className="inv-theorique">{l.stockTheorique}</span></td>
                        <td className="inv-th-num">
                          <div className="inv-qte-cell">
                            <input
                              id={`inv-qte-${l.produit}`}
                              type="number" min="0"
                              className="inv-qte-input"
                              value={l.stockCompte == null ? '' : l.stockCompte}
                              placeholder="—"
                              disabled={courant.statut !== 'en_cours'}
                              onChange={e => saisir(l.produit, e.target.value)}/>
                            {etat === 'pending' && <Loader size={11} className="inv-spin inv-save-ico"/>}
                            {etat === 'ok'      && <Check  size={11} className="inv-save-ico inv-save-ico--ok"/>}
                            {etat === 'err'     && <AlertCircle size={11} className="inv-save-ico inv-save-ico--err"/>}
                          </div>
                        </td>
                        <td className="inv-th-num">
                          {l.stockCompte == null ? <span className="inv-muted">—</span> : (
                            <span className={`inv-ecart ${l.ecart > 0 ? 'inv-ecart--pos' : l.ecart < 0 ? 'inv-ecart--neg' : ''}`}>
                              {l.ecart > 0 ? '+' : ''}{l.ecart}
                            </span>
                          )}
                        </td>
                        {courant.statut === 'valide' && (
                          <td className="inv-th-num">
                            {l.deltaApplique == null ? <span className="inv-muted">—</span> : (
                              <span
                                className={`inv-ecart ${l.deltaApplique > 0 ? 'inv-ecart--pos' : l.deltaApplique < 0 ? 'inv-ecart--neg' : ''}`}
                                title={l.stockAvantAjustement != null
                                  ? `Stock réel avant ajustement : ${l.stockAvantAjustement}`
                                  : undefined}>
                                {l.deltaApplique > 0 ? '+' : ''}{l.deltaApplique}
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ══════════ Modal création ══════════ */}
      {showCreate && (
        <>
          <div className="inv-overlay" onClick={() => setShowCreate(false)}/>
          <div className="inv-modal">
            <div className="inv-modal-head">
              <div>
                <h2 className="inv-modal-title"><ClipboardList size={15}/> Nouvel inventaire</h2>
                <p className="inv-modal-sub">Le stock théorique sera gelé dès la création.</p>
              </div>
              <button className="inv-modal-close" onClick={() => setShowCreate(false)}><X size={16}/></button>
            </div>
            <div className="inv-modal-body">
              <div className="inv-field">
                <label>Entrepôt *</label>
                <select value={newEntrepot} onChange={e => setNewEntrepot(e.target.value)} autoFocus>
                  <option value="">— Sélectionner —</option>
                  {entrepots.map(e => <option key={e._id} value={e._id}>{e.nom}</option>)}
                </select>
              </div>
              <div className="inv-field">
                <label>Note</label>
                <input type="text" value={newNote} onChange={e => setNewNote(e.target.value)}
                  placeholder="Inventaire annuel, contrôle ponctuel…"/>
              </div>
              <div className="inv-frozen">
                <Snowflake size={13}/>
                <span>Les quantités en stock au moment de la création serviront de référence pendant tout le comptage.</span>
              </div>
            </div>
            <div className="inv-modal-footer">
              <button className="inv-btn-ghost" onClick={() => setShowCreate(false)}>Annuler</button>
              <button className="inv-btn-primary" onClick={lancerInventaire} disabled={creating || !newEntrepot}>
                {creating ? <><Loader size={13} className="inv-spin"/> Gel du stock…</> : <><Snowflake size={13}/> Démarrer</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══════════ Modal écarts & validation ══════════ */}
      {showEcarts && courant && (
        <>
          <div className="inv-overlay" onClick={() => setShowEcarts(false)}/>
          <div className="inv-modal inv-modal--lg">
            <div className="inv-modal-head">
              <div>
                <h2 className="inv-modal-title"><ClipboardList size={15}/> Vérification des écarts</h2>
                <p className="inv-modal-sub">
                  {synthese.comptees} référence(s) comptée(s) · {synthese.avecEcart} avec écart
                  {synthese.restantes > 0 && ` · ${synthese.restantes} non comptée(s)`}
                  {synthese.rangements > 0 && ` · ${synthese.rangements} rangement(s) à corriger`}
                </p>
              </div>
              <button className="inv-modal-close" onClick={() => setShowEcarts(false)}><X size={16}/></button>
            </div>
            <div className="inv-modal-body">
              {synthese.restantes > 0 && (
                <div className="inv-warn">
                  <AlertCircle size={13}/>
                  <span>
                    <strong>{synthese.restantes} référence(s)</strong> n'ont pas été comptées : leur stock
                    restera inchangé après validation.
                  </span>
                </div>
              )}

              {synthese.avecEcart === 0 ? (
                <div className="inv-ok"><CheckCircle size={16}/> Aucun écart constaté sur les références comptées.</div>
              ) : (
                <table className="inv-table">
                  <thead>
                    <tr>
                      <th>Référence</th><th>Produit</th>
                      <th className="inv-th-num">Théorique</th>
                      <th className="inv-th-num">Compté</th>
                      <th className="inv-th-num">Écart</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.filter(l => l.stockCompte != null && l.ecart !== 0).map(l => (
                      <tr key={String(l.produit)}>
                        <td><span className="inv-prod-ref">{l.codeProduit || '—'}</span></td>
                        <td><span className="inv-prod-nom">{l.nomProduit}</span></td>
                        <td className="inv-th-num">{l.stockTheorique}</td>
                        <td className="inv-th-num"><strong>{l.stockCompte}</strong></td>
                        <td className="inv-th-num">
                          <span className={`inv-ecart ${l.ecart > 0 ? 'inv-ecart--pos' : 'inv-ecart--neg'}`}>
                            {l.ecart > 0 ? '+' : ''}{l.ecart}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="inv-tfoot">
                      <td colSpan="4">Écart net</td>
                      <td className="inv-th-num">
                        <span className={`inv-ecart ${synthese.ecartNet > 0 ? 'inv-ecart--pos' : synthese.ecartNet < 0 ? 'inv-ecart--neg' : ''}`}>
                          {synthese.ecartNet > 0 ? '+' : ''}{synthese.ecartNet}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}

              {/* Rangements corrigés pendant le comptage */}
              {lignesRangees.length > 0 && (
                <>
                  <h3 className="inv-section-titre"><MapPin size={13}/> Rangements corrigés</h3>
                  <p className="inv-section-sub">
                    Déjà appliqués : tout le stock de ces références a été regroupé
                    dans <strong>{courant.nomEntrepot}</strong>, sur l'emplacement constaté au comptage.
                  </p>
                  <table className="inv-table">
                    <thead>
                      <tr>
                        <th>Référence</th><th>Produit</th>
                        <th>Emplacement</th><th>Rangement corrigé</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lignesRangees.map(l => (
                        <tr key={`rang-${l.produit}`}>
                          <td><span className="inv-prod-ref">{l.codeProduit || '—'}</span></td>
                          <td><span className="inv-prod-nom">{l.nomProduit}</span></td>
                          <td><span className="inv-emp-actuel"><MapPin size={10}/> {emplacementActuel(l)}</span></td>
                          <td><span className="inv-emp-new"><ArrowRight size={10}/> {l.libelleCible || 'Non rangé'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
            {/* Confirmation obligatoire : la validation ne part jamais d'un seul clic */}
            {confirmation && (
              <div className="inv-confirm">
                <div className="inv-confirm-head">
                  <AlertCircle size={15}/> Validation définitive — confirmation requise
                </div>
                <ul className="inv-confirm-list">
                  <li>Le stock de l'entrepôt <strong>{courant.nomEntrepot}</strong> sera remplacé par les quantités comptées.</li>
                  <li><strong>{synthese.avecEcart}</strong> référence(s) seront ajustées (écart net {synthese.ecartNet > 0 ? '+' : ''}{synthese.ecartNet}).</li>
                  <li>Un mouvement de stock sera enregistré pour chaque ajustement.</li>
                  {synthese.rangements > 0 && (
                    <li>
                      <strong>{synthese.rangements}</strong> rangement(s) corrigé(s), déjà appliqué(s) au stock :
                      la validation les confirme (annuler l'inventaire les aurait rétablis).
                    </li>
                  )}
                  {synthese.restantes > 0 && (
                    <li>{synthese.restantes} référence(s) non comptée(s) resteront inchangées.</li>
                  )}
                  <li>Cette action est <strong>irréversible</strong> : l'inventaire sera clôturé.</li>
                </ul>
              </div>
            )}

            <div className="inv-modal-footer">
              {!confirmation ? (
                <>
                  <button className="inv-btn-ghost" onClick={() => setShowEcarts(false)}>Retour au comptage</button>
                  <button className="inv-btn-valider" onClick={() => setConfirmation(true)}
                    disabled={!synthese.comptees && !synthese.rangements}>
                    <Check size={14}/> Valider l'inventaire
                  </button>
                </>
              ) : (
                <>
                  <button className="inv-btn-ghost" onClick={() => setConfirmation(false)} disabled={busy}>
                    Non, revenir
                  </button>
                  <button className="inv-btn-danger" onClick={valider} disabled={busy}>
                    {busy
                      ? <><Loader size={13} className="inv-spin"/> Validation…</>
                      : <><ShieldCheck size={14}/> Oui, valider définitivement</>}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
